import { Injectable } from '@angular/core';
import { HttpRequest } from '@angular/common/http';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { filter, first, map, switchMap } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { getIsSiriusEnabled } from '@app/common/sirius-util';
import {
  selectIsImpersonating,
  selectImpersonationToken,
  selectImpersonationUserData,
  selectAuthState,
  selectCurrentToken,
} from '../store';

/**
 * Service providing authentication operations that automatically handle
 * impersonation when active.
 *
 * This service provides a unified interface for getting user data and tokens,
 * returning impersonated context when impersonation is active, or the
 * authenticated user's context otherwise.
 */
@Injectable({
  providedIn: 'root',
})
export class PlAuthContextService {
  constructor(
    private oidcSecurityService: OidcSecurityService,
    private store: Store
  ) {}

  /**
   * Live stream of the current context's user data, emitting the user object
   * itself (null until it is available).
   *
   * Prefer this over {@link getUserData} whenever you need to react to user
   * data becoming available. `getUserData` resolves to
   * `OidcSecurityService.getUserData()`, which is a one-shot snapshot of the
   * store at subscribe time: on a slow connection `isAuthenticated$` flips as
   * soon as the access token is persisted, which is BEFORE userinfo has been
   * fetched, so a snapshot taken then is null and never updates.
   */
  get userData$(): Observable<any> {
    return this.store.select(selectIsImpersonating).pipe(
      switchMap((isImpersonating) => {
        if (isImpersonating) {
          return this.store.select(selectImpersonationUserData);
        }
        // Unwrap OIDC's `UserDataResult` so both branches emit the same shape.
        return this.oidcSecurityService.userData$.pipe(
          map((result) => result?.userData)
        );
      })
    );
  }

  /**
   * Gets the current context's user data.
   * Returns impersonated user if impersonation is active, otherwise the authenticated user.
   */
  getUserData(configId?: string): Observable<any> {
    return this.store.select(selectIsImpersonating).pipe(
      switchMap((isImpersonating) => {
        if (isImpersonating) {
          return this.store.select(selectImpersonationUserData);
        }
        return this.oidcSecurityService.getUserData(configId);
      })
    );
  }

  /**
   * Gets the current context's access token for an HTTP request.
   * Returns impersonation token if active, otherwise the authenticated user's token.
   * For Sirius requests during impersonation, returns the admin's token instead.
   */
  getAccessToken(httpRequest: HttpRequest<any>): Observable<string> {
    const isSirius = this.isSiriusRequest(httpRequest);

    // Check impersonation status from the store reactively
    return this.store.select(selectIsImpersonating).pipe(
      first(),
      switchMap((isImpersonating) => {
        // Do not use impersonation token for Sirius requests
        if (isImpersonating && !isSirius) {
          return this.store.select(selectAuthState).pipe(
            filter((authState) => this.isAuthResolved(authState)),
            concatLatestFrom(() => this.store.select(selectImpersonationToken)),
            switchMap(([, token]) => of(token)),
            first()
          );
        }

        return this.store.select(selectAuthState).pipe(
          filter((authState) => this.isAuthResolved(authState)),
          concatLatestFrom(() => this.store.select(selectCurrentToken)),
          switchMap(([, token]) => of(token)),
          first()
        );
      })
    );
  }

  // In Sirius mode, requests must wait until OIDC hydration has actually
  // resolved: the store boots in 'init' before checkAuth completes, and
  // emitting then would send requests without an Authorization header
  // (PRO-2436). Django mode must not be held in 'init' at all - its auth
  // state is resolved by an intercepted request (GET /api/v1/status/) that
  // public routes never dispatch, so gating would deadlock; it relies on the
  // interceptor's 401 -> PLCheckUserStatus -> retry path instead.
  private isAuthResolved(authState: string): boolean {
    if (!getIsSiriusEnabled()) {
      return authState !== 'pending';
    }
    return authState !== 'init' && authState !== 'pending';
  }

  /**
   * Checks if currently in an impersonation context
   */
  isImpersonating(): Observable<boolean> {
    return this.store.select(selectIsImpersonating);
  }

  private isSiriusRequest(httpRequest: HttpRequest<any>): boolean {
    return httpRequest.url.startsWith(environment.sirius_url);
  }
}
